import DealsApi from "@apis/deals";
import { Category, Product, Store } from "@apis/index";
import Header from "@commons/header";
import Button from "@components/button";
import Wrapper from "@components/wrapper";
import useConfig from "@hooks/useConfig";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import React, { useEffect, useState } from "react";
import { FiFilter, FiGrid, FiList, FiPackage } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { MultiSelect } from "@components/multi-select";
import Upload from "@components/fields/upload";
import Input from "@components/fields/input";
import { Form, Formik } from "formik";
import ProductCard from "./ProductCard";
import {
  CategorySelect,
  ControlBar,
  ControlContent,
  CountBadge,
  CountLabel,
  CountValue,
  EmptyIcon,
  EmptyState,
  FilterWrapper,
  FooterActions,
  LeftControls,
  LoadingContainer,
  ProductsGrid,
  RightControls,
  SearchIconWrapper,
  SearchInput,
  SearchWrapper,
  SelectAllButton,
  SelectedInfo,
  SelectionMessage,
  Spinner,
  StatCard,
  StatsBar,
  StatTitle,
  StatValue,
  StickyFooter,
  ViewButton,
  ViewToggle,
} from "./style";

const DealProducts = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { bucketUrl } = useConfig();
  const { settings } = useGeneralSettings();

  const [deal, setDeal] = useState<any>(null);
  const [stores, setStores] = useState<any[]>([]);
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);

  // Store Configs: { storeId: { title: "", priority: 0, image: null } }
  const [storeConfigs, setStoreConfigs] = useState<
    Record<string, { title: string; priority: number; image: any }>
  >({});

  // Products grouped by store
  const [productsByStore, setProductsByStore] = useState<Record<string, any[]>>(
    {},
  );

  // Global selection map
  const [selectedProductsMap, setSelectedProductsMap] = useState<
    Record<string, any>
  >({});

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");

  // Accordion state
  const [expandedStores, setExpandedStores] = useState<string[]>([]);
  const [storeSearchTerms, setStoreSearchTerms] = useState<
    Record<string, string>
  >({});
  // Store-specific category filters
  const [storeCategoryFilters, setStoreCategoryFilters] = useState<
    Record<string, string>
  >({});

  // Filters
  const [categories, setCategories] = useState<any[]>([]);
  const [filterCategory, setFilterCategory] = useState<string>("");

  // Load Deal and Pre-populate Data
  useEffect(() => {
    if (id) {
      DealsApi.find({ id }).then((res: any) => {
        setDeal(res);
        if (res.deals && Array.isArray(res.deals)) {
          const loadedStoreIds: string[] = [];
          const loadedConfigs: any = {};
          const loadedProductMap: any = {};

          res.deals.forEach((item: any) => {
            loadedStoreIds.push(item.storeId);
            loadedConfigs[item.storeId] = {
              title: item.title,
              priority: item.priority || 0,
              image: item.image,
            };

            if (item.products && Array.isArray(item.products)) {
              item.products.forEach((p: any) => {
                // We need to map the stored format back to the UI product format
                // This is tricky because we only have 'productId' and discount info in 'products' array usually
                // But the UI expects full product details (name, price, etc.)
                // These details will be fetched when 'fetchProducts' runs because we add the storeId to selectedStoreIds
                // But we need a way to 'mark' them as selected and apply the saved discounts once they are fetched.

                // We'll use a temporary map to store saved settings (discounts) for products
                // key: productId
                loadedProductMap[p.productId] = {
                  id: p.productId,
                  isSelected: true,
                  discountType: p.discountType,
                  discountValue: p.discountValue,
                  variations: p.variations,
                };
              });
            }
          });

          setSelectedStoreIds(loadedStoreIds);
          setStoreConfigs(loadedConfigs);
          // We can't set selectedProductsMap fully yet because we lack full product data (name, image etc)
          // We will merge this loaded info in fetchProducts
          setSelectedProductsMap(loadedProductMap);
        }
      });
    }
  }, [id]);

  // Load Stores & Categories
  useEffect(() => {
    if (deal?.moduleId) {
      // Load Categories
      Category.list({
        params: { moduleId: deal.moduleId, parentId: 0 },
      })
        .then((cats: any) => {
          const list = Array.isArray(cats)
            ? cats
            : cats?.data || cats?.items || [];
          setCategories(Array.isArray(list) ? list : []);
        })
        .catch(console.error);

      // Load Stores
      Store.list({
        params: { moduleId: deal.moduleId, limit: 100 },
      })
        .then((res: any) => {
          const list = Array.isArray(res) ? res : res?.data || res?.items || [];
          setStores(Array.isArray(list) ? list : []);
        })
        .catch(console.error);
    }
  }, [deal?.moduleId]);

  // Fetch Products for Selected Stores
  const fetchProducts = async () => {
    if (!deal || selectedStoreIds.length === 0) {
      setProductsByStore({});
      return;
    }
    setLoading(true);
    try {
      const promises = selectedStoreIds.map(async (storeId) => {
        const res: any = await Product.list({
          params: {
            ...(filterCategory && { categoryId: filterCategory }),
            moduleId: deal.moduleId,
            storeId: storeId,
            limit: 50,
          },
        });
        const list = Array.isArray(res) ? res : res?.data || res?.items || [];
        const mappedList = Array.isArray(list)
          ? list.map((p: any) => {
              // Check global selection map first to see if we have saved info
              // Note: selectedProductsMap might contain partial objects from initial load (just ID + discount info)
              // OR full objects from user interaction. We need to prioritize the fetched product data (name, price)
              // but overlay the saved selection/discount state.
              const savedState = selectedProductsMap[p.id];

              if (savedState) {
                const merged = {
                  ...p, // API data first
                  isSelected: true, // It is selected
                  dealPrice: p.price, // Default deal price base
                  discountType: savedState.discountType || "percentage",
                  discountValue: savedState.discountValue || 0,
                  // Map variations, merging saved variation settings if they exist
                  variations: p.variations?.map((v: any) => {
                    const savedVar = savedState.variations?.find(
                      (sv: any) => (sv.variationId || sv.id) === v.id,
                    );
                    return {
                      ...v,
                      dealPrice: v.price,
                      discountType: savedVar?.discountType || "percentage",
                      discountValue: savedVar?.discountValue || 0,
                    };
                  }),
                };
                // Update the map with the FULL object now that we have it
                // This is a side-effect inside map, but necessary to "hydrate" the partial loaded state
                // actually we should probably do this outside or via state update, but for now:
                selectedProductsMap[p.id] = merged;
                return merged;
              }

              return {
                ...p,
                isSelected: false,
                dealPrice: p.price,
                discountType: "percentage",
                discountValue: 0,
                variations: p.variations?.map((v: any) => ({
                  ...v,
                  dealPrice: v.price,
                  discountType: "percentage",
                  discountValue: 0,
                })),
              };
            })
          : [];
        return { storeId, products: mappedList };
      });

      const results = await Promise.all(promises);
      const newProductsByStore: Record<string, any[]> = {};
      results.forEach((r) => {
        newProductsByStore[r.storeId] = r.products;
      });
      setProductsByStore(newProductsByStore);

      // Auto-expand newly selected stores
      setExpandedStores((prev) => {
        const newIds = selectedStoreIds.filter(
          (id) =>
            !prev.includes(id) && !Object.keys(productsByStore).includes(id),
        );
        return [...prev, ...newIds];
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadStoreProducts = async (
    storeId: string,
    searchTerm: string = "",
    categoryId: string = "",
  ) => {
    try {
      // setLoading(true); // Maybe local loading state?
      const res: any = await Product.list({
        params: {
          ...(categoryId && { categoryId: categoryId }),
          moduleId: deal.moduleId,
          storeId: storeId,
          limit: 50,
          search: searchTerm,
        },
      });
      const list = Array.isArray(res) ? res : res?.data || res?.items || [];
      const mappedList = Array.isArray(list)
        ? list.map((p: any) => {
            const savedState = selectedProductsMap[p.id];
            if (savedState) {
              const merged = {
                ...p,
                isSelected: true,
                dealPrice: p.price,
                discountType: savedState.discountType || "percentage",
                discountValue: savedState.discountValue || 0,
                variations: p.variations?.map((v: any) => {
                  const savedVar = savedState.variations?.find(
                    (sv: any) => (sv.variationId || sv.id) === v.id,
                  );
                  return {
                    ...v,
                    dealPrice: v.price,
                    discountType: savedVar?.discountType || "percentage",
                    discountValue: savedVar?.discountValue || 0,
                  };
                }),
              };
              selectedProductsMap[p.id] = merged;
              return merged;
            }

            return {
              ...p,
              isSelected: false,
              dealPrice: p.price,
              discountType: "percentage",
              discountValue: 0,
              variations: p.variations?.map((v: any) => ({
                ...v,
                dealPrice: v.price,
                discountType: "percentage",
                discountValue: 0,
              })),
            };
          })
        : [];

      // Merge with previously selected products from this store
      // to preserve selections when changing categories/search
      const existingProducts = productsByStore[storeId] || [];
      const selectedExisting = existingProducts.filter(
        (p) => p.isSelected && !mappedList.find((mp) => mp.id === p.id),
      );

      // Combine: newly loaded products + previously selected products not in new results
      const mergedList = [...mappedList, ...selectedExisting];

      setProductsByStore((prev) => ({
        ...prev,
        [storeId]: mergedList,
      }));
    } catch (e) {
      console.error(e);
      toast.error("Failed to search products");
    }
  };

  useEffect(() => {
    if (deal) fetchProducts();
  }, [deal, filterCategory, selectedStoreIds]);

  // Update Global Map & Local Store State Helper
  const updateGlobalMap = (product: any, storeId: string) => {
    const newMap = { ...selectedProductsMap };
    if (product.isSelected) {
      newMap[product.id] = product;
    } else {
      delete newMap[product.id];
    }
    setSelectedProductsMap(newMap);

    // Also update local state to reflect change immediately in UI
    setProductsByStore((prev) => ({
      ...prev,
      [storeId]: prev[storeId].map((p) => (p.id === product.id ? product : p)),
    }));
  };

  // Handlers
  const toggleProductSelection = (productId: string, storeId: string) => {
    const storeProducts = productsByStore[storeId] || [];
    const product = storeProducts.find((p) => p.id === productId);
    if (product) {
      const updated = { ...product, isSelected: !product.isSelected };
      updateGlobalMap(updated, storeId);
    }
  };

  const updateProductDiscount = (
    productId: string,
    field: string,
    value: any,
    storeId: string,
  ) => {
    const storeProducts = productsByStore[storeId] || [];
    const product = storeProducts.find((p) => p.id === productId);
    if (product) {
      let processedValue = value;
      if (field === "discountValue") {
        const numVal = parseFloat(value) || 0;
        const basePrice = product.priceBeforeDeal || product.price;
        if (product.discountType === "percentage") {
          processedValue = Math.min(100, Math.max(0, numVal));
        } else {
          processedValue = Math.min(basePrice, Math.max(0, numVal));
        }
      }
      const updated = { ...product, [field]: processedValue };
      // If selected, update global map. Always update local.
      setProductsByStore((prev) => ({
        ...prev,
        [storeId]: prev[storeId].map((p) => (p.id === productId ? updated : p)),
      }));
      if (updated.isSelected) {
        const newMap = { ...selectedProductsMap };
        newMap[updated.id] = updated;
        setSelectedProductsMap(newMap);
      }
    }
  };

  const updateVariationDiscount = (
    productId: string,
    variationId: string,
    field: string,
    value: any,
    storeId: string,
  ) => {
    const storeProducts = productsByStore[storeId] || [];
    const product = storeProducts.find((p) => p.id === productId);
    if (product && product.variations) {
      const updated = {
        ...product,
        variations: product.variations.map((v: any) => {
          if ((v.id || v._id) === variationId) {
            let processedValue = value;
            if (field === "discountValue") {
              const numVal = parseFloat(value) || 0;
              const basePrice = v.priceBeforeDeal || v.price;
              if (v.discountType === "percentage") {
                processedValue = Math.min(100, Math.max(0, numVal));
              } else {
                processedValue = Math.min(basePrice, Math.max(0, numVal));
              }
            }
            return { ...v, [field]: processedValue };
          }
          return v;
        }),
      };
      setProductsByStore((prev) => ({
        ...prev,
        [storeId]: prev[storeId].map((p) => (p.id === productId ? updated : p)),
      }));
      if (updated.isSelected) {
        const newMap = { ...selectedProductsMap };
        newMap[updated.id] = updated;
        setSelectedProductsMap(newMap);
      }
    }
  };

  const handleSelectAllInStore = (storeId: string) => {
    const storeProducts = productsByStore[storeId] || [];

    // Filter products that are eligible for selection (not conflicted)
    // A product is conflicted if it's in another active deal
    const eligibleProducts = storeProducts.filter((p) => {
      const activeDeal = p.activeDeal;
      const isConflict =
        activeDeal && (id ? String(activeDeal.dealId) !== String(id) : true);
      return !isConflict;
    });

    if (eligibleProducts.length === 0) return;

    // Check if all eligible products are currently selected
    const allSelected = eligibleProducts.every((p) => p.isSelected);

    const newStoreProducts = storeProducts.map((p) => {
      const activeDeal = p.activeDeal;
      const isConflict =
        activeDeal && (id ? String(activeDeal.dealId) !== String(id) : true);

      // If product is conflicted, do not change its state
      if (isConflict) return p;

      return {
        ...p,
        isSelected: !allSelected,
      };
    });

    setProductsByStore((prev) => ({
      ...prev,
      [storeId]: newStoreProducts,
    }));

    // Update Global Map
    setSelectedProductsMap((prev) => {
      const newMap = { ...prev };
      newStoreProducts.forEach((p) => {
        if (p.isSelected) newMap[p.id] = p;
        else delete newMap[p.id];
      });
      return newMap;
    });
  };

  const handleSave = async () => {
    const items = selectedStoreIds
      .map((storeId) => {
        const config = storeConfigs[storeId] || {
          title: "",
          priority: 0,
          image: null,
        };
        const allStoreProducts = productsByStore[storeId] || [];
        const selectedProducts = allStoreProducts.filter((p) => p.isSelected);

        if (selectedProducts.length === 0) return null;

        return {
          storeId: storeId,
          title: config.title,
          priority: config.priority,
          image: config.image,
          products: selectedProducts.map((p: any) => ({
            productId: p.id,
            discountType: p.discountType,
            discountValue: Number(p.discountValue),
            variations: p.variations?.map((v: any) => ({
              variationId: v.id || v._id,
              discountType: v.discountType,
              discountValue: Number(v.discountValue),
            })),
          })),
        };
      })
      .filter(Boolean);

    if (items.length === 0) {
      toast.error("Please select at least one product");
      return;
    }

    const payload = {
      dealId: id,
      items: items,
    };

    console.log("Submitting payload:", payload);
    setSubmitting(true);
    try {
      await DealsApi.addProducts(payload);
      toast.success("Products added for selected stores");
      navigate("/admin/deals");
    } catch (e: any) {
      toast.error(e.message || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStoreExpand = (storeId: string) => {
    setExpandedStores((prev) =>
      prev.includes(storeId)
        ? prev.filter((id) => id !== storeId)
        : [...prev, storeId],
    );
  };

  const totalSelectedCount = Object.keys(selectedProductsMap).length;

  return (
    <Wrapper>
      <Header title={`Manage Products: ${deal?.title || "Loading..."}`} />

      {/* Store Selection */}
      <div style={{ marginBottom: "20px" }}>
        <div
          style={{
            marginBottom: "8px",
            fontWeight: 600,
            color: "#374151",
            fontSize: "14px",
          }}
        >
          Select Stores
        </div>
        <div style={{ maxWidth: "100%" }}>
          <MultiSelect
            options={stores.map((s: any) => ({
              value: s.id,
              label: s.storeName,
              image: s.logo
                ? `${bucketUrl}/${s.logo}`
                : s.avatar
                  ? `${bucketUrl}/${s.avatar}`
                  : "/pngs/default-user.png",
            }))}
            selected={selectedStoreIds}
            onChange={setSelectedStoreIds}
            placeholder="Select stores to load products..."
          />
        </div>
      </div>

      <StatsBar>
        <StatCard>
          <StatTitle>Total Selected</StatTitle>
          <StatValue color="#10b981">{totalSelectedCount}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Categories</StatTitle>
          <StatValue>{categories.length}</StatValue>
        </StatCard>
      </StatsBar>

      {/* ControlBar Removed from here */}

      {/* Content: Store Accordions */}
      {selectedStoreIds.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <FiPackage size={28} />
          </EmptyIcon>
          <div
            style={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#1f2937",
              marginBottom: "8px",
            }}
          >
            No Stores Selected
          </div>
          <div style={{ fontSize: "14px", color: "#6b7280" }}>
            Please select one or more stores to view products
          </div>
        </EmptyState>
      ) : loading ? (
        <LoadingContainer>
          <Spinner />
          <div style={{ fontSize: "14px", color: "#6b7280" }}>
            Loading products from selected stores...
          </div>
        </LoadingContainer>
      ) : (
        selectedStoreIds.map((storeId) => {
          const store = stores.find((s) => s.id === storeId);
          const storeProducts = productsByStore[storeId] || [];
          const filteredStoreProducts = storeProducts;

          const isExpanded = expandedStores.includes(storeId);
          const config = storeConfigs[storeId] || {
            title: "",
            priority: 0,
            image: null,
          };

          if (!store) return null;

          return (
            <div
              key={storeId}
              style={{
                marginBottom: "20px",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                overflow: "hidden",
                background: "white",
              }}
            >
              {/* Accordion Header */}
              <div
                onClick={() => toggleStoreExpand(storeId)}
                style={{
                  padding: "16px 20px",
                  background: isExpanded ? "#f0fdf4" : "white", // subtle green background when expanded
                  borderBottom: isExpanded ? "1px solid #dcfce7" : "none",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "background 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isExpanded
                    ? "#ecfdf5"
                    : "#f9fafb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isExpanded
                    ? "#f0fdf4"
                    : "white";
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      background: "#e5e7eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "#6b7280",
                      overflow: "hidden",
                    }}
                  >
                    {store.logo || store.avatar ? (
                      <img
                        src={`${bucketUrl}/${store.logo || store.avatar}`}
                        alt={store.storename}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      store.name.substring(0, 1)
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "16px",
                        color: "#1f2937",
                      }}
                    >
                      {store.storeName}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        marginTop: "2px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {store.email && <span>{store.email}</span>}
                      {store.email && (store.phone || store.phoneNumber) && (
                        <span style={{ fontSize: "8px" }}>●</span>
                      )}
                      {(store.phone || store.phoneNumber) && (
                        <span>{store.phone || store.phoneNumber}</span>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#059669",
                      background: "#d1fae5",
                      padding: "2px 10px",
                      borderRadius: "12px",
                    }}
                  >
                    {filteredStoreProducts.length} Products
                  </div>
                  {/* Show selected count if collapsed */}
                  {!isExpanded && (
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      (
                      {productsByStore[storeId]?.filter((p) => p.isSelected)
                        .length || 0}{" "}
                      selected)
                    </div>
                  )}
                </div>

                <div style={{ color: isExpanded ? "#059669" : "#9ca3af" }}>
                  {isExpanded ? (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="18 15 12 9 6 15"></polyline>
                    </svg>
                  ) : (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  )}
                </div>
              </div>

              {/* Accordion Content */}
              {isExpanded && (
                <div style={{ padding: "20px" }}>
                  <Formik
                    initialValues={{
                      [`title_${storeId}`]: config.title,
                      [`priority_${storeId}`]: config.priority,
                      [`image_${storeId}`]: config.image,
                    }}
                    onSubmit={() => {}}
                    enableReinitialize
                  >
                    {({ setFieldValue, values }) => (
                      <>
                        {/* Store Config Section */}
                        <div
                          style={{
                            marginBottom: "24px",
                            paddingBottom: "24px",
                            borderBottom: "1px dashed #e5e7eb",
                          }}
                        >
                          <div
                            style={{
                              marginBottom: "16px",
                              display: "flex",
                              gap: "10px",
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <Input
                                name={`title_${storeId}`}
                                label="Deal Title"
                                placeholder={`Enter deal title for ${store.name}`}
                                value={values[`title_${storeId}`]}
                                onChange={(val: any) => {
                                  setFieldValue(`title_${storeId}`, val);
                                  setStoreConfigs((prev) => ({
                                    ...prev,
                                    [storeId]: {
                                      ...prev[storeId],
                                      title: val,
                                    },
                                  }));
                                }}
                              />
                            </div>
                            <div style={{ width: "120px" }}>
                              <Input
                                name={`priority_${storeId}`}
                                label="Priority"
                                type="number"
                                placeholder="0"
                                value={values[`priority_${storeId}`] || 0}
                                onChange={(val: any) => {
                                  setFieldValue(`priority_${storeId}`, val);
                                  setStoreConfigs((prev) => ({
                                    ...prev,
                                    [storeId]: {
                                      ...prev[storeId],
                                      priority: val,
                                    },
                                  }));
                                }}
                              />
                            </div>
                          </div>
                          <div style={{ width: "100%" }}>
                            <Upload
                              name={`image_${storeId}`}
                              label="Cover Image"
                              variant="banner"
                              height={290}
                              styleWrapper="width: 100%;"
                              onChange={(val: any) => {
                                setFieldValue(`image_${storeId}`, val);
                                setStoreConfigs((prev) => ({
                                  ...prev,
                                  [storeId]: {
                                    ...prev[storeId],
                                    image: val,
                                  },
                                }));
                              }}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </Formik>

                  {/* Select All Bar */}
                  <div
                    style={{
                      marginBottom: "16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <SearchWrapper style={{ width: "300px", margin: 0 }}>
                      <SearchInput
                        placeholder="Search products globally in store..."
                        value={storeSearchTerms[storeId] || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setStoreSearchTerms((prev) => ({
                            ...prev,
                            [storeId]: val,
                          }));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            loadStoreProducts(
                              storeId,
                              storeSearchTerms[storeId],
                              storeCategoryFilters[storeId],
                            );
                          }
                        }}
                        onBlur={() => {
                          // Optional: search on blur too
                          loadStoreProducts(
                            storeId,
                            storeSearchTerms[storeId],
                            storeCategoryFilters[storeId],
                          );
                        }}
                      />
                      <SearchIconWrapper
                        onClick={() =>
                          loadStoreProducts(
                            storeId,
                            storeSearchTerms[storeId],
                            storeCategoryFilters[storeId],
                          )
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="11" cy="11" r="8" />
                          <path d="m21 21-4.35-4.35" />
                        </svg>
                      </SearchIconWrapper>
                    </SearchWrapper>

                    {/* Category Filter per store */}
                    <div style={{ marginRight: "auto", marginLeft: "10px" }}>
                      <FilterWrapper>
                        <FiFilter />
                        <CategorySelect
                          value={storeCategoryFilters[storeId] || ""}
                          onChange={(e) => {
                            const newCat = e.target.value;
                            setStoreCategoryFilters((prev) => ({
                              ...prev,
                              [storeId]: newCat,
                            }));
                            loadStoreProducts(
                              storeId,
                              storeSearchTerms[storeId],
                              newCat,
                            );
                          }}
                        >
                          <option value="">All Categories</option>
                          {categories?.map((c: any) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </CategorySelect>
                      </FilterWrapper>
                    </div>

                    {/* View Toggle */}
                    <div style={{ marginRight: "10px" }}>
                      <ViewToggle>
                        <ViewButton
                          active={viewMode === "list"}
                          onClick={() => setViewMode("list")}
                        >
                          <FiList size={16} />
                        </ViewButton>
                        <ViewButton
                          active={viewMode === "grid"}
                          onClick={() => setViewMode("grid")}
                        >
                          <FiGrid size={16} />
                        </ViewButton>
                      </ViewToggle>
                    </div>

                    <Button
                      title={
                        storeProducts.every((p) => p.isSelected) &&
                        storeProducts.length > 0
                          ? "Deselect All in Store"
                          : "Select All in Store"
                      }
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleSelectAllInStore(storeId);
                      }}
                      height={32}
                      styleButton="font-size: 12px;"
                    />
                  </div>

                  {/* Products Grid */}
                  {filteredStoreProducts.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#9ca3af",
                      }}
                    >
                      No products match your search in this store.
                    </div>
                  ) : (
                    <ProductsGrid viewMode={viewMode}>
                      {filteredStoreProducts.map((item) => (
                        <ProductCard
                          key={item.id}
                          item={item}
                          toggleSelection={(pid) =>
                            toggleProductSelection(pid, storeId)
                          }
                          updateProductDiscount={(pid, f, v) =>
                            updateProductDiscount(pid, f, v, storeId)
                          }
                          updateVariationDiscount={(pid, vid, f, v) =>
                            updateVariationDiscount(pid, vid, f, v, storeId)
                          }
                          bucketUrl={bucketUrl}
                          settings={settings}
                          currentDealId={id}
                        />
                      ))}
                    </ProductsGrid>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}

      <StickyFooter>
        <SelectedInfo>
          <CountBadge>
            <CountLabel>Selected Products</CountLabel>
            <CountValue>{totalSelectedCount}</CountValue>
          </CountBadge>
          <SelectionMessage>
            {totalSelectedCount > 0
              ? `Ready to add ${totalSelectedCount} products. Review discounts.`
              : "Select products to add."}
          </SelectionMessage>
        </SelectedInfo>

        <FooterActions>
          <Button title="Cancel" onClick={() => navigate(-1)} width={120} />
          <Button
            title="Save Deal Products"
            onClick={handleSave}
            bg="primary"
            width={200}
            loading={submitting}
            disabled={totalSelectedCount === 0 || submitting}
          />
        </FooterActions>
      </StickyFooter>
    </Wrapper>
  );
};

export default DealProducts;
